import groupBy from "lodash/groupBy";
import * as types from "../actions/actionTypes";

import {
  IAssociation,
  IAssociationResource,
} from "../../types/association/Association";
import { Role } from "../../types/persons/Role";
import { IAssociationState, InitialState } from "./state";

const getUpdatedStateWithNewAssociations = (
  state: IAssociationState,
  associations: IAssociation[],
) =>
  !!associations && associations.length > 0
    ? {
        ...state,
        map: {
          ...state.map,
          ...associations.reduce((mp: object, assc) => {
            const existingAssoc = assc._id in state.map && state.map[assc._id];
            mp[assc._id] = existingAssoc
              ? { ...existingAssoc, ...assc }
              : { ...assc };
            return mp;
          }, {}),
        },
      }
    : state;

const getSetWithNewIds = (ids: string[], newIds: string[]) =>
  Array.from(new Set(ids.concat(newIds)));

const reducer = {
  [types.PERSONS_GET_SELF_FULFILLED]: (
    state: IAssociationState,
    { person, associations },
  ) => {
    const adminAssociations = person?.associations
      ?.filter((a) => Role.hasRole(a.role, Role.administrative))
      ?.map((a) => a.associationId);
    const newAssociations = adminAssociations
      ? Array.from(new Set<string>([...adminAssociations, ...state.admin]))
      : state.admin;
    return {
      ...getUpdatedStateWithNewAssociations(state, associations),
      admin: newAssociations,
    };
  },
  [types.ASSOCIATIONS_GET_MANAGEABLE_FULFILLED]: (
    state: IAssociationState,
    associations: IAssociation[],
  ) => ({
    ...getUpdatedStateWithNewAssociations(state, associations),
    admin: getSetWithNewIds(
      state.admin,
      associations.map((a) => a._id),
    ),
  }),
  [types.ASSOCIATIONS_UPDATE_FULFILLED]: (
    state: IAssociationState,
    association: IAssociation,
  ) => ({
    ...getUpdatedStateWithNewAssociations(state, [association]),
    admin: getSetWithNewIds(state.admin, [association._id]),
  }),
  [types.ASSOCIATIONS_UPDATE_CONFIG_FULFILLED]: (
    state: IAssociationState,
    association: IAssociation,
  ) => ({
    ...getUpdatedStateWithNewAssociations(state, [association]),
    admin: getSetWithNewIds(state.admin, [association._id]),
  }),
  [types.SHIFTS_GET_MANAGEABLE_FULFILLED]: (
    state: IAssociationState,
    { associations },
  ) => getUpdatedStateWithNewAssociations(state, associations),
  [types.PERSONS_GET_FULL_PERSON_BY_ID_FULFILLED]: (
    state: IAssociationState,
    { associations },
  ) => getUpdatedStateWithNewAssociations(state, associations),
  [types.PERSONS_GET_MANAGEABLE_FULFILLED]: (
    state: IAssociationState,
    { associations },
  ) => ({
    ...getUpdatedStateWithNewAssociations(state, associations),
  }),
  [types.ASSOCIATIONS_GET_MY_FULFILLED]: getUpdatedStateWithNewAssociations,
  [types.SHIFTS_GET_SHIFT_FULFILLED]: (
    state: IAssociationState,
    { associations },
  ) => getUpdatedStateWithNewAssociations(state, associations),
  [types.ASSOCIATIONS_GET_RESOURCES_FULFILLED]: (
    state: IAssociationState,
    resources: IAssociationResource[],
  ) => ({
    ...state,
    resources: {
      ...state.resources,
      ...groupBy(resources, (a) => a.association),
    },
  }),
};

export default function associationsReducer(
  state = InitialState.associations,
  action,
) {
  return action.type in reducer
    ? reducer[action.type](state, action.payload)
    : state;
}
